/**
 * Email click-throughs, end to end.
 *
 * Mandrill is told (utils/email/core.ts) to append Google Analytics parameters
 * to every link on our own domains, with the template name as the campaign:
 * `?utm_source=...&utm_medium=email&utm_campaign=<template-name>`. That covers
 * the links a sender passes as merge vars AND the ones baked into the template
 * HTML (logo, footer), which the app never sees.
 *
 * The landing page reads those parameters back (hooks/useAnalytics.ts) and
 * records one `email_click` row in ClickHouse `events`, keyed by template, so
 * /dash/emails can put clicks next to sends. Mandrill's own click tracking is
 * deliberately NOT used: it rewrites every link through mandrillapp.com and
 * only reports back through a webhook we do not have.
 */

import { emailTestForTemplate } from './ui-tests/registry';

/** The hosts whose links Mandrill tags. Anything else (app stores, Stripe) is left alone. */
export const EMAIL_LINK_DOMAINS: readonly string[] = [
  'sellwild.com',
  'www.sellwild.com',
  'new.sellwild.com',
  'dev.sellwild.com',
];

/** The utm_medium Mandrill writes for its Google Analytics tagging. */
export const EMAIL_UTM_MEDIUM = 'email';

/** The UI test arm an email was sent in, as its links carry it. */
export interface EmailTestServing {
  test: string;
  arm: string;
  run: string;
  phase: 'test' | 'shipped';
}

export interface EmailClick {
  /** The Mandrill template the link came from, e.g. `seller-discount-digest`. */
  template: string;
  /** Where it landed, without the query string. */
  path: string;
  /** Present when the email was in a UI test (utils/ui-tests/email-arm.ts). */
  uiTest?: EmailTestServing;
}

/** Template names are kebab-case slugs; anything else is not ours and is not recorded. */
const TEMPLATE_NAME = /^[a-z0-9][a-z0-9_-]{0,159}$/i;

/** Between the template and the arm, run and phase in a tagged campaign. No template name has it. */
const PART = '--';
const PHASE_CODE = { test: 't', shipped: 's' } as const;

/**
 * The utm_campaign Mandrill writes onto an email's links: the template, and
 * for an email in a UI test, `<template>--<arm>--<run>--<t|s>`, so a click
 * says which arm it came from without anything stored per send.
 */
export function emailCampaign(template: string, uiTest?: EmailTestServing | null): string {
  if (!uiTest) return template;
  return [template, uiTest.arm, uiTest.run, PHASE_CODE[uiTest.phase]].join(PART);
}

/** The template and, when tagged and its test still exists, the UI test arm. */
function readCampaign(campaign: string): { template: string; uiTest?: EmailTestServing } {
  const parts = campaign.split(PART);
  if (parts.length !== 4) return { template: campaign };
  const [template, arm, run, code] = parts;
  const phase = code === PHASE_CODE.test ? 'test' : code === PHASE_CODE.shipped ? 'shipped' : null;
  const test = emailTestForTemplate(template);
  // A tag whose test is gone still counts as a click on its template.
  if (!phase || !test || !arm || !run) return { template };
  return { template, uiTest: { test, arm, run, phase } };
}

/**
 * The email click this URL records, or null when it did not come from one of
 * our emails. Pure, so the landing hook and its tests agree on what counts.
 */
export function emailClickFromUrl(href: string): EmailClick | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }

  if (url.searchParams.get('utm_medium') !== EMAIL_UTM_MEDIUM) return null;

  const campaign = (url.searchParams.get('utm_campaign') ?? '').trim();
  if (!TEMPLATE_NAME.test(campaign)) return null;

  const { template, uiTest } = readCampaign(campaign.toLowerCase());
  return { template, path: url.pathname.slice(0, 500), ...(uiTest ? { uiTest } : {}) };
}
